import { matchRoutes } from 'react-router-dom';

/**
 * The context we attach to a bug report or a crash: which page, which build,
 * and a reference the user can read back over the phone.
 *
 * The one rule that matters here: **a report names the matched route PATTERN,
 * never the URL.** Patient pages are `/p/:token`, so `window.location.pathname`
 * is a working magic link — a URL that opens somebody's leave paperwork — and
 * putting it in a support ticket hands it to whoever reads the ticket queue.
 * ADR 0004 step 2. The backend (`backend/error_context.py`) re-checks the shape
 * and withholds anything that is not pattern-shaped, so a mistake here is
 * embarrassing rather than a disclosure.
 */

// Mirrors the <Route path="…"> values in App.js. It is a second list on purpose
// — react-router's `useMatches` needs a data router and this app uses
// <BrowserRouter> — so ADD NEW ROUTES HERE TOO. Drift is safe, not silent: an
// unlisted path reports 'unmatched' rather than leaking the path it could not
// name.
export const ROUTE_PATTERNS = [
  '/', '/login', '/signup', '/forgot-password', '/reset-password/:token', '/invite/:token',
  '/terms', '/privacy', '/hipaa', '/baa',
  '/intake/:pid', '/p/:token', '/p/:token/form', '/p/:token/status', '/p/:token/document',
  '/start', '/r/:token', '/claim/:token', '/patient-requests',
  '/nurse', '/nurse/fast-review', '/nurse/archive', '/nurse/submission/:sid',
  '/doctor', '/doctor/fast-sign', '/doctor/archive', '/doctor/review/:sid',
  '/admin', '/admin/catalog', '/admin/users', '/admin/billing', '/admin/settings', '/admin/audit',
  '/owner', '/owner/practices', '/owner/practices/:pid', '/owner/organisations',
  '/owner/support', '/owner/feedback', '/owner/audit', '/owner/settings',
];

const ROUTE_OBJECTS = ROUTE_PATTERNS.map((path) => ({ path }));

export function routePattern(pathname) {
  try {
    const matches = matchRoutes(ROUTE_OBJECTS, pathname || '/');
    return matches?.[matches.length - 1]?.route?.path || 'unmatched';
  } catch {
    return 'unmatched';
  }
}

// Which code was running. REACT_APP_BUILD_SHA wins when a deploy sets it;
// otherwise CRA's content-hashed bundle filename identifies the build exactly,
// with nothing to configure and nothing to keep in sync.
export function buildId() {
  const declared = process.env.REACT_APP_BUILD_SHA;
  if (declared) return String(declared).slice(0, 40);
  try {
    const src = document.querySelector('script[src*="/static/js/main."]')?.getAttribute('src') || '';
    const hash = src.match(/main\.([a-z0-9]+)\.js/i)?.[1];
    return hash ? `bundle-${hash}` : 'dev';
  } catch {
    return 'dev';
  }
}

// One short reference per browser tab session, so a crash report, the ticket the
// user opens afterwards and anything else from the same sitting line up. Short
// enough to read down a phone line.
const REF_KEY = 'fmla_ref_v1';

export function correlationId() {
  try {
    let ref = sessionStorage.getItem(REF_KEY);
    if (!ref) {
      const bytes = new Uint8Array(4);
      window.crypto.getRandomValues(bytes);
      ref = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
      sessionStorage.setItem(REF_KEY, ref);
    }
    return ref;
  } catch {
    return 'no-ref';
  }
}

export function errorContext(pathname, extra = {}) {
  return {
    route: routePattern(pathname),
    build: buildId(),
    correlation_id: correlationId(),
    viewport: `${window.innerWidth}x${window.innerHeight}`,
    user_agent: navigator.userAgent,
    ...extra,
  };
}
